import type { JSX } from 'react';
import { Badge, Box, Heading, SectionHeader } from '@ksyos/design-system';
import { useMemo, useState } from 'react';
import type { IProcessAction, Process as ProcessType } from '@common/interfaces';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { Breakpoints, ProcessStatus, ServiceCode } from '@common/enums';
import { ProcessTodoList } from '@frontend/components/pages/episodeCategory/carePath/process/ProcessTodoList';
import {
    getAvailableActions,
    getCanTakeOverTasksFromColleague,
    takeOverTasksFromColleague,
} from '@frontend/services/api/processesApiService';
import { getSelectedLanguage } from '@frontend/translate'; // The `Process` type needs an alias, because otherwise it conflicts with the `Process` component
import { useViewportWidth } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDeepCompareEffect } from '@frontend/utils/hooks/useDeepCompareEffect';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { useSplitViewContext } from '@frontend/utils/hooks/useSplitViewContext';
import { useUserSessionStorage } from '@frontend/utils/hooks/useUserSessionStorage';
import { isSplitViewEnabledForCarePath } from '@frontend/utils/isSplitViewEnabledForCarePath';
import type { ProcessActionButtonItem } from './processActionButtonItem';
import { PatientEpisodeCategoryCarePathContext } from '../patientEpisodeCategoryCarePathContext';
import { ProcessActionButtonRow } from './ProcessActionButtonRow';
import { ProcessActivities } from './ProcessActivities';
import { ProcessContextProvider } from './ProcessContextProvider';

type ProcessProps = Readonly<{
    process: ProcessType;
}>;

// By default, the last activity of a process is expanded, this is disabled for the following services
const servicesWithLastActivityCollapsed: ServiceCode[] = [
    ServiceCode.BariPre,
    ServiceCode.BariPost,
    ServiceCode.OSAS,
    ServiceCode.RespiAller,
];

export function Process(props: ProcessProps): JSX.Element {
    const { process } = props;
    const abortSignal = useAbortSignal();
    const { episodeCategory } = usePatientEpisodeCategoryContext();
    const {
        activeModifyActivity,
        activeProcessAction,
        activeStartingPoint,
        saveProcessAction,
        setActiveProcessAction,
    } = useDefinedContext(PatientEpisodeCategoryCarePathContext);
    const { setSplitViewContent } = useSplitViewContext();
    const { width } = useViewportWidth();
    const [availableProcessActions, setAvailableProcessActions] = useState<IProcessAction[]>([]);
    const [canTakeOverProcessTasks, setCanTakeOverProcessTasks] = useState<boolean>();
    const buttonItems = useMemo(getButtonItems, [availableProcessActions, canTakeOverProcessTasks, process]);
    const [expandedProcessActivityIds, setExpandedProcessActivityIds] = useUserSessionStorage<string[]>(
        `${process.id}_expandedProcessActivityIds`,
        getInitiallyExpandedProcessActivityIds()
    );
    const [hasRenderedSplitViewContent, setHasRenderedSplitViewContent] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const carePathHasSplitView = isSplitViewEnabledForCarePath(episodeCategory);
    const isProcessActive = process.status === ProcessStatus.active;
    const isEditing =
        activeStartingPoint !== undefined || activeProcessAction !== undefined || activeModifyActivity !== undefined;
    const shouldRenderTodos = isProcessActive && !isLoading && !isEditing;

    useDeepCompareEffect(() => {
        const loadAll = async () => {
            setIsLoading(true);
            await Promise.all([loadAvailableProcessActions(process.id), loadCanTakeOverProcessTasks(process.id)]);
            setIsLoading(false);
        };
        loadAll();
    }, [process]);

    /*
     * For now we assume that only one process is active at the same time in a care path.
     * If this is not the case anymore in the future, then the rendering logic in this hook should be changed so that a
     * higher level component can render a todo list for each active process and handle the state better.
     */
    useDeepCompareEffect(() => {
        if (!carePathHasSplitView) {
            return;
        }

        // This is to handle the scenario when a process is not active anymore after the split view content has already
        // been rendered. For example, when a caregiver performs the cancel process activity.
        if (!isProcessActive && hasRenderedSplitViewContent) {
            setSplitViewContent(null);
            setHasRenderedSplitViewContent(false);
        } else if (shouldRenderTodos) {
            const splitViewProcessTodoList = (
                <Box paddingX={4}>
                    <Heading
                        marginY={9}
                        size="medium"
                    >
                        Taken en acties
                    </Heading>
                    <ProcessTodoList
                        isRenderedInSplitView={true}
                        items={buttonItems}
                        processId={process.id}
                    />
                </Box>
            );
            setSplitViewContent(splitViewProcessTodoList);
            setHasRenderedSplitViewContent(true);
        }
    }, [buttonItems, carePathHasSplitView, isProcessActive, shouldRenderTodos]);

    return (
        <ProcessContextProvider
            availableProcessActions={availableProcessActions}
            expandedProcessActivityIds={expandedProcessActivityIds}
            expandProcessActivity={expandProcessActivity}
            process={process}
            setExpandedProcessActivityIds={setExpandedProcessActivityIds}
        >
            <Box
                display="flex"
                gap={20}
            >
                <Box flex="1 1 auto">
                    {/* TODO: CS-23298 Remove box once `SectionHeader` has a built-in `badge` option */}
                    <Box
                        alignItems="flex-start"
                        display="flex"
                        gap={4}
                        sx={{ marginTop: -4 }}
                    >
                        <SectionHeader
                            isSensitiveData={true}
                            noGutterOnMobile={true}
                            subtitle={`PID ${process.processNumber}`}
                            title={getServiceLabel(process.service.code, 'processTitle')}
                        />
                        {isProcessActive && process.isPaused && (
                            <Box mt={[6, null, null, 9]}>
                                <Badge
                                    color="orange"
                                    icon="warning"
                                    text="Gepauzeerd"
                                />
                            </Box>
                        )}
                    </Box>
                    {shouldRenderTodos &&
                        (carePathHasSplitView
                            ? width < Breakpoints.xlarge && (
                                  <ProcessTodoList
                                      items={buttonItems}
                                      processId={process.id}
                                  />
                              )
                            : (availableProcessActions.length > 0 || canTakeOverProcessTasks) && (
                                  <ProcessActionButtonRow
                                      // Filter out the process action buttons for actions in progress
                                      buttonItems={buttonItems.filter(
                                          (buttonItem) => !buttonItem.processWorkflowStepId && !buttonItem.isPaused
                                      )}
                                  />
                              ))}
                    {!isLoading && <ProcessActivities />}
                </Box>
            </Box>
        </ProcessContextProvider>
    );

    function getButtonItems(): ProcessActionButtonItem[] {
        if (process.status !== ProcessStatus.active) {
            return [];
        }
        const processActionButtonItems = availableProcessActions.map(computeProcessActionButtonItem) ?? [];

        if (canTakeOverProcessTasks) {
            processActionButtonItems.push({
                key: 'takeFromColleague',
                label: getSelectedLanguage().request.statusAction_takeFromColleague,
                onClick: () => takeOverProcessTasks(),
            });
        }

        return processActionButtonItems;
    }

    async function loadAvailableProcessActions(processId: string): Promise<void> {
        const nextProcessActions = await getAvailableActions(processId, { includePaused: true }, abortSignal);
        setAvailableProcessActions((currentProcessActions) => {
            return JSON.stringify(currentProcessActions) === JSON.stringify(nextProcessActions)
                ? currentProcessActions
                : nextProcessActions;
        });

        if (activeProcessAction && !activeProcessAction.processWorkflowStepId) {
            const activeProcessActionUpdate = nextProcessActions.find((action) => action.id === activeProcessAction.id);
            setActiveProcessAction(activeProcessActionUpdate);
        }
    }

    async function loadCanTakeOverProcessTasks(processId: string): Promise<void> {
        const canTakeOver = await getCanTakeOverTasksFromColleague(processId, abortSignal);
        setCanTakeOverProcessTasks(canTakeOver);
    }

    function computeProcessActionButtonItem(action: IProcessAction): ProcessActionButtonItem {
        const actionActivityIds = action.activities?.map((activity) => activity.id) ?? [];
        // Find the first of the process activities that is included in the possible current action activities
        const activeActivity = process.activities.find((activity) => actionActivityIds.includes(activity.id));

        return {
            badge: action.processWorkflowStepId ? 'Concept' : undefined,
            isPaused: action.isPaused,
            key: action.processWorkflowStepId ?? action.serviceWorkflowStep.id,
            label: action.label,
            onClick: () => startProcessAction(action),
            organizationName: activeActivity?.performerOrganization?.name,
            processTaskCreatedDatetime: action.processTaskCreatedDatetime,
            processWorkflowStepId: action.processWorkflowStepId,
            serviceWorkflowStepId: action.id,
        };
    }

    async function startProcessAction(action: IProcessAction): Promise<void> {
        if (action.activities) {
            setActiveProcessAction(action);
        } else {
            await saveProcessAction(process.id, {
                actionId: action.id,
                serviceWorkflowStepId: action.serviceWorkflowStep.id,
            });
        }
    }

    async function takeOverProcessTasks(): Promise<void> {
        await takeOverTasksFromColleague(process.id, abortSignal);
        setCanTakeOverProcessTasks(false);
        await loadAvailableProcessActions(process.id);
    }

    function getInitiallyExpandedProcessActivityIds(): string[] {
        if (servicesWithLastActivityCollapsed.includes(process.service.code)) {
            return [];
        }
        const lastActivityId = process.activities[process.activities.length - 1]?.id;

        return lastActivityId ? [lastActivityId] : [];
    }

    function expandProcessActivity(processActivityId: string) {
        setExpandedProcessActivityIds([...expandedProcessActivityIds, processActivityId]);
    }
}
